import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { PLAYER_GRAPHICS, TEAM_GRAPHICS, TOURNAMENT_GRAPHICS } from "constants/phase";
import { getImageUrlArray } from "utils/thumbnail-editor";
import { extractGraphicData } from "utils/thumbnail-editor/shared-utils";

import { getTournamentDataById, getTournaments, getTournamentsList } from "./actions";

export const tournamentEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity.tournamentId,
});

const initialState = tournamentEntityAdapter.getInitialState({
  totalCount: 0,
  modals: {},
  tournamentDetails: null,
});

export const tournamentSlice = createSlice({
  name: "tournament",
  initialState: initialState,
  reducers: {
    showTournamentAddModal: (state, action) => {
      state.modals.tournamentAddModal = action.payload;
    },
    showTournamentDeleteModal: (state, action) => {
      state.modals.tournamentDeleteModal = action.payload;
    },
    setTournamentDetails: (state, action) => {
      state.tournamentDetails = {
        ...state.tournamentDetails,
        ...action.payload,
      };
    },
    populateTournamentDetails: (state, action) => {
      const tournamentDetails = action.payload?.tournamentDetails;

      const playerNameMappings = action.payload?.playerNameMappings;
      const teamMappings = action.payload?.teamMappings;
      const textMappings = action.payload?.textMappings;

      const idMappings = action.payload?.idMappings;
      const isEdit = action.payload?.isEdit;

      const tournamentData = {
        displayName: tournamentDetails?.displayName,
        tournamentId: tournamentDetails?.tournamentId,
        tournamentName: tournamentDetails?.tournamentName,
        tournamentImages: tournamentDetails?.tournamentImages?.map((image) => ({
          _id: image?._id?._id,
          graphicTags: image?._id?.graphicTags,
          contentType: image?._id?.contentType,
          type: image?._id?.graphicType,
          url: image?._id?.imgUrl,
          graphicTitle: image?._id?.graphicTitle,
        })),
        category: tournamentDetails?.category,
        _id: tournamentDetails?._id,
      };

      const teamData = tournamentDetails?.teams?.map((team) => ({
        name: team?.name,
        teamId: team?.teamId,
        teamImages: team?.teamImages?.map((image) => extractGraphicData(image)),
        category: team?.category,
        playerIds: team?.playerIds,
        country: team?.country,
        _id: team?._id,
      }));

      const findTeamById = (id) => teamData?.find((team) => team?.teamId === id) || {};

      const team_1 = findTeamById(isEdit ? idMappings?.team_1 : teamMappings?.team_1_id);
      const team_2 = findTeamById(isEdit ? idMappings?.team_2 : teamMappings?.team_2_id);

      const playerDataRaw =
        tournamentDetails?.teams?.flatMap((team) =>
          team?.players?.map((player) => ({
            name: player?.name,
            firstName: player?.firstName,
            middleName: player?.middleName,
            lastName: player?.lastName,
            id: player?.id,
            playerImages: player?.playerImages?.map((image) => extractGraphicData(image)),
            country: player?.country,
            tagType: player?.tagType,
            category: player?.category,
            _id: player?._id,
          })),
        ) || [];

      const playerMap = new Map();

      playerDataRaw.forEach((player) => {
        if (player._id && !playerMap.has(player._id)) {
          playerMap.set(player._id, player);
        }
      });
      const playerData = Array.from(playerMap.values());

      const findPlayer = (idMapping, nameMapping) => {
        if (idMapping && isEdit) {
          return playerData?.find((player) => player?._id === idMapping) || {};
        }

        return playerData?.find((player) => player?.name === nameMapping) || {};
      };

      const player_1 = findPlayer(idMappings?.player_1, playerNameMappings?.player_1);
      const player_2 = findPlayer(idMappings?.player_2, playerNameMappings?.player_2);
      const player_3 = findPlayer(idMappings?.player_3, playerNameMappings?.player_3);
      const player_4 = findPlayer(idMappings?.player_4, playerNameMappings?.player_4);

      const tournament_logo = getImageUrlArray(tournamentData?.tournamentImages, TOURNAMENT_GRAPHICS.TOURNAMENT_LOGO);
      const sponsor_logo = getImageUrlArray(tournamentData?.tournamentImages, TOURNAMENT_GRAPHICS.SPONSOR_LOGO);
      const broadcaster_logo = getImageUrlArray(tournamentData?.tournamentImages, TOURNAMENT_GRAPHICS.BROADCASTER_LOGO);

      const team_1_logo = getImageUrlArray(team_1?.teamImages, TEAM_GRAPHICS.LOGO);
      const team_1_full_image = getImageUrlArray(team_1?.teamImages, TEAM_GRAPHICS.FULL_IMAGE);
      const team_1_coach_image = getImageUrlArray(team_1?.teamImages, TEAM_GRAPHICS.COACH_IMAGE);
      const team_2_logo = getImageUrlArray(team_2?.teamImages, TEAM_GRAPHICS.LOGO);
      const team_2_full_image = getImageUrlArray(team_2?.teamImages, TEAM_GRAPHICS.FULL_IMAGE);
      const team_2_coach_image = getImageUrlArray(team_2?.teamImages, TEAM_GRAPHICS.COACH_IMAGE);

      const player_1_full_shot = getImageUrlArray(player_1?.playerImages, PLAYER_GRAPHICS.FULL_IMAGE);
      const player_1_half_shot = getImageUrlArray(player_1?.playerImages, PLAYER_GRAPHICS.HALF_IMAGE);
      const player_2_full_shot = getImageUrlArray(player_2?.playerImages, PLAYER_GRAPHICS.FULL_IMAGE);
      const player_2_half_shot = getImageUrlArray(player_2?.playerImages, PLAYER_GRAPHICS.HALF_IMAGE);
      const player_3_full_shot = getImageUrlArray(player_3?.playerImages, PLAYER_GRAPHICS.FULL_IMAGE);
      const player_3_half_shot = getImageUrlArray(player_3?.playerImages, PLAYER_GRAPHICS.HALF_IMAGE);
      const player_4_full_shot = getImageUrlArray(player_4?.playerImages, PLAYER_GRAPHICS.FULL_IMAGE);
      const player_4_half_shot = getImageUrlArray(player_4?.playerImages, PLAYER_GRAPHICS.HALF_IMAGE);

      const imageMappings = {
        tournament_logo: { name: "tournament_logo", imgs: tournament_logo, id: tournamentData?.tournamentId },
        sponsor_logo: { name: "sponsor_logo", imgs: sponsor_logo, id: tournamentData?.tournamentId },
        broadcaster_logo: { name: "broadcaster_logo", imgs: broadcaster_logo, id: tournamentData?.tournamentId },
        team_1_logo: { name: team_1?.name, imgs: team_1_logo, id: team_1?.teamId },
        team_1_full_image: { name: team_1?.name, imgs: team_1_full_image, id: team_1?.teamId },
        team_1_coach_image: { name: team_1?.name, imgs: team_1_coach_image, id: team_1?.teamId },
        team_1_country_flag: {
          name: team_1?.name,
          imgs: [team_1?.country?.flagUrl].filter(Boolean),
          id: team_1?.teamId,
        },
        team_2_logo: { name: team_2?.name, imgs: team_2_logo, id: team_2?.teamId },
        team_2_full_image: { name: team_2?.name, imgs: team_2_full_image, id: team_2?.teamId },
        team_2_coach_image: { name: team_2?.name, imgs: team_2_coach_image, id: team_2?.teamId },
        team_2_country_flag: {
          name: team_2?.name,
          imgs: [team_2?.country?.flagUrl].filter(Boolean),
          id: team_2?.teamId,
        },
        player_1_full_shot: { name: player_1?.name, imgs: player_1_full_shot, id: player_1?._id },
        player_1_half_shot: { name: player_1?.name, imgs: player_1_half_shot, id: player_1?._id },
        player_1_country_flag: {
          name: player_1?.name,
          imgs: [player_1?.country?.flagUrl].filter(Boolean),
          id: player_1?._id,
        },
        player_2_full_shot: { name: player_2?.name, imgs: player_2_full_shot, id: player_2?._id },
        player_2_half_shot: { name: player_2?.name, imgs: player_2_half_shot, id: player_2?._id },
        player_2_country_flag: {
          name: player_2?.name,
          imgs: [player_2?.country?.flagUrl].filter(Boolean),
          id: player_2?._id,
        },
        player_3_full_shot: { name: player_3?.name, imgs: player_3_full_shot, id: player_3?._id },
        player_3_half_shot: { name: player_3?.name, imgs: player_3_half_shot, id: player_3?._id },
        player_3_country_flag: {
          name: player_3?.name,
          imgs: [player_3?.country?.flagUrl].filter(Boolean),
          id: player_3?._id,
        },
        player_4_full_shot: { name: player_4?.name, imgs: player_4_full_shot, id: player_4?._id },
        player_4_half_shot: { name: player_4?.name, imgs: player_4_half_shot, id: player_4?._id },
        player_4_country_flag: {
          name: player_4?.name,
          imgs: [player_4?.country?.flagUrl].filter(Boolean),
          id: player_4?._id,
        },
      };

      let nameMappings = state.tournamentDetails?.nameMappings || {};
      if (!isEdit) {
        nameMappings = {
          player_1: {
            full_name: player_1?.name,
            first_name: player_1?.firstName,
            last_name: player_1?.lastName,
            middle_name: player_1?.middleName,
          },
          player_2: {
            full_name: player_2?.name,
            first_name: player_2?.firstName,
            last_name: player_2?.lastName,
            middle_name: player_2?.middleName,
          },
          player_3: {
            full_name: player_3?.name,
            first_name: player_3?.firstName,
            last_name: player_3?.lastName,
            middle_name: player_3?.middleName,
          },
          player_4: {
            full_name: player_4?.name,
            first_name: player_4?.firstName,
            last_name: player_4?.lastName,
            middle_name: player_4?.middleName,
          },
        };
      }

      const playerIds = {
        player_1: player_1?._id,
        player_2: player_2?._id,
        player_3: player_3?._id,
        player_4: player_4?._id,
      };

      const teamIds = {
        team_1: team_1?.teamId,
        team_2: team_2?.teamId,
      };

      state.tournamentDetails = {
        tournament: tournamentData,
        teams: teamData,
        players: playerData,
        imageMappings: imageMappings,
        nameMappings: nameMappings,
        textMappings: textMappings,
        idMappings: {
          ...playerIds,
          ...teamIds,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTournaments.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload.entities?.tournament) {
          tournamentEntityAdapter.setAll(state, action.payload.entities.tournament);
          state.totalCount = action.payload.totalCount || 0;
        } else {
          state.totalCount = 0;
          tournamentEntityAdapter.removeAll(state);
        }
      }
    });
    builder.addCase(getTournamentsList.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload?.data) {
          tournamentEntityAdapter.setAll(state, action.payload.data);
        } else {
          state.ids = [];
        }
      }
    });
    builder.addCase(getTournamentDataById.fulfilled, (state, action) => {
      if (action.payload.success) {
        tournamentEntityAdapter.setOne(state, action.payload.data);
      }
    });
  },
});

export const { showTournamentAddModal, showTournamentDeleteModal, setTournamentDetails, populateTournamentDetails } =
  tournamentSlice.actions;

export default tournamentSlice.reducer;
